import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Alert,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import { Warning, CheckCircle, Speed, AccessTime, TrendingUp, TrendingDown, Error } from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import { websiteService } from '../../../../api/cansense/websiteApi';
import { WebsiteHistory as WebsiteHistoryType, Website } from '../types/website';

interface WebsiteHistoryProps {
  website: Website;
}

type HistoryPeriod = '1h' | '24h' | '7d' | '30d';

const periodLabels: Record<HistoryPeriod, string> = {
  '1h': 'Last Hour',
  '24h': 'Last 24 Hours',
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
};

export function WebsiteHistoryComponent({ website }: WebsiteHistoryProps) {
  const theme = useTheme();
  const [period, setPeriod] = useState<HistoryPeriod>('24h');
  const [history, setHistory] = useState<WebsiteHistoryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await websiteService.getWebsiteHistory(website.id, period);
        setHistory(data);
      } catch (err) {
        setError('Failed to load history data');
        console.error('Failed to fetch website history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [website.id, period]);

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: HistoryPeriod | null) => {
    if (newPeriod) {
      setPeriod(newPeriod);
    }
  };

  const getUptimeColor = (uptime: number) => {
    if (uptime >= 99.9) return theme.palette.success.main;
    if (uptime >= 99) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!history) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No history data available
      </Alert>
    );
  }

  return (
    <Box>
      {/* Period Selector */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Monitoring History</Typography>
        <ToggleButtonGroup value={period} exclusive onChange={handlePeriodChange} size="small">
          {(Object.keys(periodLabels) as HistoryPeriod[]).map((p) => (
            <ToggleButton key={p} value={p}>
              {periodLabels[p]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Stats Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 3 }}>
        {/* Uptime */}
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Uptime
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: getUptimeColor(history.uptime_percent),
              fontWeight: 700,
            }}
          >
            {history.uptime_percent.toFixed(2)}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={history.uptime_percent}
            sx={{
              mt: 1,
              height: 6,
              borderRadius: 3,
              bgcolor: alpha(getUptimeColor(history.uptime_percent), 0.1),
              '& .MuiLinearProgress-bar': {
                bgcolor: getUptimeColor(history.uptime_percent),
                borderRadius: 3,
              },
            }}
          />
        </Paper>

        {/* Avg Response Time */}
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Avg Response
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {history.avg_response_time_ms}
            <Typography component="span" variant="body2" color="text.secondary">
              ms
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, gap: 0.5 }}>
            <Speed sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Average
            </Typography>
          </Box>
        </Paper>

        {/* Min Response Time */}
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Fastest
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
            {history.min_response_time_ms}
            <Typography component="span" variant="body2" color="text.secondary">
              ms
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, gap: 0.5 }}>
            <TrendingDown sx={{ fontSize: 14, color: theme.palette.success.main }} />
            <Typography variant="caption" color="text.secondary">
              Min
            </Typography>
          </Box>
        </Paper>

        {/* Max Response Time */}
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Slowest
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: history.max_response_time_ms > 2000 ? theme.palette.error.main : theme.palette.warning.main,
            }}
          >
            {history.max_response_time_ms}
            <Typography component="span" variant="body2" color="text.secondary">
              ms
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, gap: 0.5 }}>
            <TrendingUp
              sx={{
                fontSize: 14,
                color: history.max_response_time_ms > 2000 ? theme.palette.error.main : theme.palette.warning.main,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Max
            </Typography>
          </Box>
        </Paper>

        {/* Total Checks */}
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Total Checks
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {history.total_checks.toLocaleString()}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, gap: 0.5 }}>
            <CheckCircle sx={{ fontSize: 14, color: theme.palette.success.main }} />
            <Typography variant="caption" color="success.main">
              {history.total_checks - history.failed_checks} passed
            </Typography>
          </Box>
        </Paper>

        {/* Failed Checks */}
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Failed Checks
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: history.failed_checks > 0 ? theme.palette.error.main : theme.palette.success.main,
            }}
          >
            {history.failed_checks}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, gap: 0.5 }}>
            {history.failed_checks > 0 ? (
              <>
                <Error sx={{ fontSize: 14, color: theme.palette.error.main }} />
                <Typography variant="caption" color="error.main">
                  {((history.failed_checks / history.total_checks) * 100).toFixed(2)}% failure rate
                </Typography>
              </>
            ) : (
              <>
                <CheckCircle sx={{ fontSize: 14, color: theme.palette.success.main }} />
                <Typography variant="caption" color="success.main">
                  No failures
                </Typography>
              </>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Response Time Chart */}
      {history.response_times.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Response Time
          </Typography>
          <Paper sx={{ p: 2 }}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart
                data={history.response_times.map((pt) => ({
                  time: new Date(pt.timestamp).getTime(),
                  value: pt.value,
                }))}
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="responseTimeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  scale="time"
                  tickFormatter={(ts) => {
                    const d = new Date(ts);
                    return period === '1h' || period === '24h'
                      ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
                      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                  }}
                  tick={{ fontSize: 11 }}
                  stroke={theme.palette.text.secondary}
                />
                <YAxis
                  unit="ms"
                  tick={{ fontSize: 11 }}
                  stroke={theme.palette.text.secondary}
                  width={55}
                />
                <RechartsTooltip
                  labelFormatter={(ts) => new Date(ts as number).toLocaleString()}
                  formatter={(value: number) => [`${value}ms`, 'Response Time']}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                {history.avg_response_time_ms && (
                  <ReferenceLine
                    y={history.avg_response_time_ms}
                    stroke={theme.palette.warning.main}
                    strokeDasharray="4 4"
                    label={{
                      value: `avg ${history.avg_response_time_ms}ms`,
                      position: 'insideTopRight',
                      fill: theme.palette.warning.main,
                      fontSize: 10,
                    }}
                  />
                )}
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={theme.palette.primary.main}
                  fill="url(#responseTimeGradient)"
                  strokeWidth={1.5}
                  dot={false}
                  activeDot={{ r: 3, strokeWidth: 1 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Incidents Timeline */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Incidents ({history.incidents.length})
      </Typography>

      {history.incidents.length === 0 ? (
        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.success.main, 0.05),
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
          }}
        >
          <CheckCircle sx={{ fontSize: 48, color: theme.palette.success.main, mb: 1 }} />
          <Typography color="success.main">No incidents during this period</Typography>
          <Typography variant="caption" color="text.secondary">
            All {history.total_checks} checks passed successfully
          </Typography>
        </Paper>
      ) : (
        <Timeline position="alternate">
          {history.incidents.map((incident, index) => (
            <TimelineItem key={index}>
              <TimelineOppositeContent color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {formatTimestamp(incident.started_at)}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot color="error">
                  <Warning sx={{ fontSize: 16 }} />
                </TimelineDot>
                {index < history.incidents.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: theme.palette.error.main }}>
                    Downtime: {formatDuration(incident.duration_seconds)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {incident.error}
                  </Typography>
                  {incident.ended_at && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
                      <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Recovered at {formatTimestamp(incident.ended_at)}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
    </Box>
  );
}

export default WebsiteHistoryComponent;
